import React, { useState, useReducer, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { Textarea } from './ui/Textarea';
import { Badge } from './ui/Badge';
import { Progress } from './ui/Progress';
import { sendRequest } from '../api';
import { cn } from '../utils';

import { 
  Calendar, 
  Clock, 
  Hash, 
  Video, 
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Zap,
  Loader2,
  ArrowLeft,
  ListVideo,
  Users,
  PartyPopper,
  ExternalLink
} from 'lucide-react';
import VideoGrid from './VideoGrid';
import PlatformToggle from './PlatformToggle';
import ScheduleCalendar from './ScheduleCalendar';
import HashtagSuggestions from './HashtagSuggestions';
import CaptionTemplates from './CaptionTemplates';
import VideoCard from './VideoCard';
import { useBeforeUnload } from 'react-use';
import { Input } from './ui/Input';

// Add storage key constant
const WIZARD_STORAGE_KEY = 'multicrosspost_wizard_state';

const WIZARD_GUIDE_STEPS = [
  {
    title: "Select Your Videos",
    description: "Choose the videos you want to schedule. You can select multiple videos to schedule them all at once.",
    icon: <Video className="w-5 h-5" />
  },
  {
    title: "Add Captions",
    description: "Write engaging captions for each video. Use our quick templates and hashtag suggestions to save time.",
    icon: <Hash className="w-5 h-5" />
  },
  {
    title: "Choose Platforms",
    description: "Select which social media platforms you want to post to (Instagram, TikTok, YouTube).",
    icon: <Users className="w-5 h-5" />
  },
  {
    title: "Set Schedule",
    description: "Choose between batch scheduling, individual times, or automated posting schedules.",
    icon: <Calendar className="w-5 h-5" />
  }
];

const wizardReducer = (state, action) => {
  let newState;
  
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_VIDEOS':
      return {
        ...state,
        videos: action.payload,
        selectedVideos: action.payload.map(v => v.id)
      };
    case 'TOGGLE_VIDEO':
      return {
        ...state,
        selectedVideos: state.selectedVideos.includes(action.payload)
          ? state.selectedVideos.filter(id => id !== action.payload)
          : [...state.selectedVideos, action.payload]
      };
    case 'UPDATE_CAPTION':
      return {
        ...state,
        captions: {
          ...state.captions,
          [action.payload.id]: action.payload.content
        }
      };
    case 'SET_SCHEDULE':
      return {
        ...state,
        schedules: {
          ...state.schedules,
          [action.payload.platform]: action.payload.schedule
        }
      };
    case 'UPDATE_PROGRESS':
      return {
        ...state,
        postingProgress: action.payload
      };
    case 'SET_ACTIVE_VIDEO':
      return {
        ...state,
        activeVideoId: action.payload
      };
    case 'RESTORE_STATE':
      return { ...state, ...action.payload };
    case 'RESET_STATE':
      return initialWizardState;
    default:
      return state;
  }
};

const initialWizardState = {
  currentStep: 0,
  videos: [],
  selectedVideos: [],
  captions: {},
  hashtags: [],
  schedules: {},
  postingProgress: 0,
  activeVideoId: null
};

const SPRING_CONFIG = {
  type: "spring",
  stiffness: 300,
  damping: 30
};

const CARD_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: SPRING_CONFIG
  }
};

function MultiCrossPostWizard({ initialVideos = [], onComplete }) {
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [scheduleType, setScheduleType] = useState('batch');
  const [customTimes, setCustomTimes] = useState({});
  const [isPosting, setIsPosting] = useState(false);
  const [automationType, setAutomationType] = useState('new');
  const [automations, setAutomations] = useState([]);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [selectedDays, setSelectedDays] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoadingAutomations, setIsLoadingAutomations] = useState(false);
  const [newAutomationData, setNewAutomationData] = useState({
    name: '',
    description: '',
    goal: 'reach',
    timeRange: {
      start: '09:00',
      end: '21:00'
    },
    postsPerDay: 3
  });
  const [isCreatingAutomation, setIsCreatingAutomation] = useState(false);
  const [showGuide, setShowGuide] = useState(() => {
    const hasSeenGuide = localStorage.getItem('has_seen_wizard_guide');
    return !hasSeenGuide;
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successDetails, setSuccessDetails] = useState(null);

  const steps = [
    { title: 'Create Content', icon: <Hash /> },
    { title: 'Schedule', icon: <Calendar /> },
    { title: 'Review', icon: <CheckCircle2 /> }
  ];

  useEffect(() => {
    if (initialVideos?.length > 0) {
      dispatch({ type: 'SET_VIDEOS', payload: initialVideos });
    }
  }, [initialVideos]);

  // Initialize selected days for each platform with all days selected
  useEffect(() => {
    const initialDays = {};
    selectedPlatforms.forEach(platform => {
      initialDays[platform] = {
        Mon: true,
        Tue: true,
        Wed: true,
        Thu: true,
        Fri: true,
        Sat: true,
        Sun: true
      };
    });
    setSelectedDays(initialDays);
  }, [selectedPlatforms]);

  const toggleDay = (platform, day) => {
    setSelectedDays(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [day]: !prev[platform]?.[day]
      }
    }));
  };

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (state.videos.length > 0) {
      const savedState = {
        currentStep: state.currentStep,
        videos: state.videos,
        selectedVideos: state.selectedVideos,
        captions: state.captions,
        schedules: state.schedules,
        activeVideoId: state.activeVideoId,
        selectedPlatforms,
        scheduleType,
        customTimes,
        automationType,
        selectedAutomation,
        selectedDays,
      };
      localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(savedState));
      setHasUnsavedChanges(true);
    }
  }, [
    state.currentStep,
    state.videos,
    state.selectedVideos,
    state.captions,
    state.schedules,
    state.activeVideoId,
    selectedPlatforms,
    scheduleType,
    customTimes,
    automationType,
    selectedAutomation,
    selectedDays
  ]);

  // Restore state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem(WIZARD_STORAGE_KEY);
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        dispatch({ type: 'RESTORE_STATE', payload: parsedState });
        setSelectedPlatforms(parsedState.selectedPlatforms || []);
        setScheduleType(parsedState.scheduleType || 'batch');
        setCustomTimes(parsedState.customTimes || {});
        setAutomationType(parsedState.automationType || 'new');
        setSelectedAutomation(parsedState.selectedAutomation || null);
        setSelectedDays(parsedState.selectedDays || {});
      } catch (error) {
        console.error('Error restoring wizard state:', error);
      }
    }
  }, []);

  // Fetch automations when component mounts and when automation type changes
  useEffect(() => {
    const fetchAutomations = async () => {
      if (scheduleType === 'automation' && automationType === 'existing') {
        setIsLoadingAutomations(true);
        try {
          console.log('Fetching automations...');
          const response = await sendRequest("/automations", "GET");
          console.log('Automations response:', response);
          
          if (response?.automations) {
            setAutomations(response.automations);
          } else {
            // Fallback to mock data if API is not ready
            setAutomations([
              {
                id: 1,
                name: "Peak Hours Posting",
                description: "Auto-post during highest engagement times",
                goal: "reach",
                schedule: "3x per day",
                nextPost: "Today at 6:00 PM",
                status: "active",
                platforms: ["instagram", "tiktok"]
              },
              {
                id: 2,
                name: "Weekend Content",
                description: "Schedule weekend content for maximum reach",
                goal: "engagement",
                schedule: "Weekends only",
                nextPost: "Saturday at 2:00 PM",
                status: "active",
                platforms: ["instagram"]
              }
            ]);
          }
        } catch (error) {
          console.error("Failed to fetch automations:", error);
          // Use mock data on error as well
          setAutomations([
            {
              id: 1,
              name: "Peak Hours Posting",
              description: "Auto-post during highest engagement times",
              goal: "reach",
              schedule: "3x per day",
              nextPost: "Today at 6:00 PM",
              status: "active",
              platforms: ["instagram", "tiktok"]
            },
            {
              id: 2,
              name: "Weekend Content",
              description: "Schedule weekend content for maximum reach",
              goal: "engagement",
              schedule: "Weekends only",
              nextPost: "Saturday at 2:00 PM",
              status: "active",
              platforms: ["instagram"]
            }
          ]);
        } finally {
          setIsLoadingAutomations(false);
        }
      }
    };

    fetchAutomations();
  }, [scheduleType, automationType]);

  // Add a debug log when automations change
  useEffect(() => {
    console.log('Current automations:', automations);
  }, [automations]);

  // Update the handleComplete function
  const handleComplete = async (result) => {
    if (result.success) {
      setSuccessDetails(result);
      setShowSuccess(true);
      localStorage.removeItem(WIZARD_STORAGE_KEY);
      setHasUnsavedChanges(false);
    }
    onComplete?.(result);
  };

  // Show warning when trying to leave with unsaved changes
  useBeforeUnload(
    useCallback(
      (event) => {
        if (hasUnsavedChanges) {
          event.preventDefault();
          return 'You have unsaved changes. Are you sure you want to leave?';
        }
      },
      [hasUnsavedChanges]
    )
  );

  // Add cleanup on unmount
  useEffect(() => {
    return () => {
      // Only clean up if we've completed successfully
      if (!hasUnsavedChanges) {
        localStorage.removeItem(WIZARD_STORAGE_KEY);
      }
    };
  }, [hasUnsavedChanges]);

  const handlePost = async () => {
    if (!selectedPlatforms.length) {
      alert('Please select at least one platform');
      return;
    }

    if (Object.keys(state.captions).length === 0) {
      alert('Please add captions to your videos');
      return;
    }

    setIsPosting(true);
    try {
      let payload;
      
      if (scheduleType === 'automation') {
        if (automationType === 'new') {
          payload = {
            name: newAutomationData.name,
            description: newAutomationData.description || '',
            goal: newAutomationData.goal,
            platform_settings: selectedPlatforms.map(platform => ({
              platform_name: platform,
              time_range: {
                start_time: newAutomationData.timeRange.start,
                end_time: newAutomationData.timeRange.end
              },
              posts_per_day: newAutomationData.postsPerDay,
              days: selectedDays[platform]
            })),
            video_ids: state.selectedVideos
          };
        } else {
          payload = {
            automation_id: selectedAutomation,
            video_ids: state.selectedVideos
          };
        }
      } else {
        payload = {
          videos: state.selectedVideos.map(id => ({
            id,
            caption: state.captions[id] || '',
            platforms: selectedPlatforms,
            schedule: scheduleType === 'batch' 
              ? state.schedules 
              : {
                  times: customTimes[id] || {},
                  platforms: selectedPlatforms.reduce((acc, platform) => ({
                    ...acc,
                    [platform]: customTimes[id]?.[platform] || null
                  }), {})
                }
          }))
        };
      }

      console.log('Sending post request with payload:', payload);
      const endpoint = scheduleType === 'automation' 
        ? (automationType === 'new' ? '/automations' : `/automations/${selectedAutomation}/add-videos`)
        : '/batch-posts';

      const response = await sendRequest(endpoint, "POST", payload);
      console.log('Post response:', response);
      
      // Set success state immediately for automation
      if (scheduleType === 'automation') {
        setSuccessDetails({
          success: true,
          automationId: response?.id || selectedAutomation,
          type: 'automation',
          videoCount: state.selectedVideos.length,
          platforms: selectedPlatforms
        });
        setShowSuccess(true);
        localStorage.removeItem(WIZARD_STORAGE_KEY);
        setHasUnsavedChanges(false);
      } else if (response?.id) {
        // For batch/individual posts
        setSuccessDetails({
          success: true,
          batchId: response.id,
          type: scheduleType,
          videoCount: state.selectedVideos.length,
          platforms: selectedPlatforms
        });
        setShowSuccess(true);
        localStorage.removeItem(WIZARD_STORAGE_KEY);
        setHasUnsavedChanges(false);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to schedule posts:', error);
      alert('Failed to schedule posts: ' + (error.message || 'Please try again'));
    } finally {
      setIsPosting(false);
    }
  };

  // Add a resume banner if there's saved state
  const hasSavedProgress = Boolean(localStorage.getItem(WIZARD_STORAGE_KEY));

  const handleCreateAutomation = async () => {
    if (!newAutomationData.name) {
      alert('Please enter a name for your automation');
      return;
    }

    // Validate selected days
    const hasSelectedDays = selectedPlatforms.every(platform => 
      Object.values(selectedDays[platform] || {}).some(selected => selected)
    );
    if (!hasSelectedDays) {
      alert('Please select at least one day for each platform');
      return;
    }

    setIsCreatingAutomation(true);
    try {
      const payload = {
        automation: {
          name: newAutomationData.name,
          description: newAutomationData.description || '',
          goal: newAutomationData.goal,
          platform_settings: selectedPlatforms.map(platform => ({
            platform_name: platform,
            time_range: {
              start_time: newAutomationData.timeRange.start,
              end_time: newAutomationData.timeRange.end
            },
            posts_per_day: newAutomationData.postsPerDay,
            days: selectedDays[platform]
          })),
          video_ids: state.selectedVideos
        }
      };

      console.log('Creating automation with payload:', payload);
      const response = await sendRequest("/automations", "POST", payload);
      console.log('Automation creation response:', response);

      if (response?.automation?.id) {
        // Add the new automation to the list
        setAutomations(prev => [...prev, response.automation]);
        
        // Clear saved state since we're moving to next step
        localStorage.removeItem(WIZARD_STORAGE_KEY);
        
        // Move to next step
        dispatch({ type: 'SET_STEP', payload: state.currentStep + 1 });
      } else {
        throw new Error('Failed to create automation');
      }
    } catch (error) {
      console.error("Failed to create automation:", error);
      alert('Failed to create automation: ' + (error.message || 'Please try again'));
    } finally {
      setIsCreatingAutomation(false);
    }
  };

  useEffect(() => {
    if (!showGuide) {
      localStorage.setItem('has_seen_wizard_guide', 'true');
    }
  }, [showGuide]);

  const SuccessState = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <div className="bg-card rounded-2xl max-w-lg w-full p-6 space-y-6 border border-border shadow-lg">
        <div className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <PartyPopper className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">Successfully Scheduled!</h2>
          <p className="text-muted-foreground">
            {successDetails?.batchId ? (
              `Your ${state.selectedVideos.length} videos have been scheduled across ${selectedPlatforms.length} platform${selectedPlatforms.length > 1 ? 's' : ''}`
            ) : (
              `Your videos have been added to the automation "${automations.find(a => a.id === successDetails?.automationId)?.name}"`
            )}
          </p>
        </div>

        <div className="bg-muted rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Videos Scheduled</span>
            <span className="font-medium text-foreground">{state.selectedVideos.length}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Platforms</span>
            <div className="flex gap-1">
              {selectedPlatforms.map(platform => (
                <Badge key={platform} variant="secondary">{platform}</Badge>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Schedule Type</span>
            <span className="font-medium text-foreground capitalize">{scheduleType}</span>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            onClick={() => window.location.href = '/calendar'}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Calendar className="w-4 h-4 mr-2" />
            View in Calendar
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setShowSuccess(false);
              dispatch({ type: 'RESET_STATE' });
              window.location.reload();
            }}
            className="border-border text-muted-foreground hover:bg-muted"
          >
            Schedule More Videos
          </Button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className={cn(
      "bg-background min-h-screen",
      // Remove iOS-specific padding and use consistent spacing
      "p-4",
      // Responsive padding
      "sm:p-6 md:p-8",
      "overflow-x-hidden" // Prevent horizontal scroll during animations
    )}>
      {showGuide && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl max-w-2xl w-full p-6 space-y-6 border border-border shadow-lg">
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-foreground">Welcome to Multi-Post Wizard! 🎉</h2>
              <p className="text-muted-foreground">Let's help you schedule your content across multiple platforms</p>
            </div>

            <div className="grid gap-4">
              {WIZARD_GUIDE_STEPS.map((step, index) => (
                <div key={step.title} className="flex items-start gap-4 p-4 bg-muted rounded-xl">
                  <div className="p-2 bg-background rounded-lg shadow-sm border border-border">
                    {step.icon}
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground flex items-center gap-2">
                      Step {index + 1}: {step.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mt-1">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowGuide(false)}
                className="text-muted-foreground"
              >
                Skip Guide
              </Button>
              <Button
                onClick={() => setShowGuide(false)}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-[1400px] mx-auto">
        {hasSavedProgress && state.currentStep === 0 && (
          <div className="mb-6 bg-muted border border-border rounded-xl p-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-background shadow-sm border border-border">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">Resume Your Progress</h4>
                  <p className="text-sm text-muted-foreground">You have a saved draft from your last session</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    localStorage.removeItem(WIZARD_STORAGE_KEY);
                    dispatch({ type: 'RESET_STATE' });
                    window.location.reload();
                  }}
                  className="text-muted-foreground"
                >
                  Start Fresh
                </Button>
                <Button
                  onClick={() => {
                    const savedState = localStorage.getItem(WIZARD_STORAGE_KEY);
                    if (savedState) {
                      const parsedState = JSON.parse(savedState);
                      dispatch({ type: 'RESTORE_STATE', payload: parsedState });
                    }
                  }}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Resume
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Progress Header */}
        <motion.div 
          className="mb-8 flex items-center gap-4 overflow-x-auto pb-2 hide-scrollbar"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={SPRING_CONFIG}
        >
          {steps.map((step, index) => (
            <React.Fragment key={step.title}>
              <motion.button
                onClick={() => dispatch({ type: 'SET_STEP', payload: index })}
                className={cn(
                  "flex items-center gap-2 px-6 py-3 rounded-xl transition-all",
                  "whitespace-nowrap flex-shrink-0",
                  state.currentStep >= index 
                    ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25' 
                    : 'bg-card text-muted-foreground hover:bg-muted border border-border'
                )}
                whileTap={{ scale: 0.95 }}
              >
                {step.icon}
                <span className="font-medium">{step.title}</span>
              </motion.button>
              {index < steps.length - 1 && (
                <ChevronRight className="h-6 w-6 text-muted-foreground flex-shrink-0" />
              )}
            </React.Fragment>
          ))}
        </motion.div>

        {/* Step Content */}
        <motion.div 
          className="bg-card rounded-2xl shadow-xl border border-border overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...SPRING_CONFIG, delay: 0.2 }}
        >
          <AnimatePresence mode='wait'>
            {state.currentStep === 0 && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                key="step-2"
                className="h-[800px] flex flex-col"
              >
                {/* Mobile Header */}
                <div className="md:hidden px-4 py-3 border-b border-gray-100 bg-white sticky top-0 z-20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => dispatch({ type: 'SET_ACTIVE_VIDEO', payload: null })}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <ArrowLeft className="h-5 w-5" />
                      </button>
                      <h1 className="text-xl font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                        Edit Caption
                      </h1>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => dispatch({ type: 'SET_STEP', payload: state.currentStep + 1 })}
                      className="text-sm font-medium text-purple-600"
                    >
                      Next
                    </Button>
                  </div>
                </div>
                
                {/* Desktop Header */}
                <h3 className="text-xl font-semibold mb-4 hidden md:block p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-transparent">
                  Create Content
                </h3>
                
                <div className="flex gap-6 h-full relative p-4 md:p-6">
                  {/* Video List Sidebar - Desktop */}
                  <div className="hidden md:block w-72 lg:w-80 flex-shrink-0 border-r border-gray-100">
                    <div className="pr-6 space-y-3 h-full overflow-y-auto">
                      {state.selectedVideos.map((videoId, index) => (
                        <VideoListItem 
                          key={videoId}
                          video={state.videos.find(v => v.id === videoId)}
                          index={index}
                          isActive={state.activeVideoId === videoId}
                          hasCaption={Boolean(state.captions[videoId])}
                          onClick={() => dispatch({ type: 'SET_ACTIVE_VIDEO', payload: videoId })}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Video List - Mobile */}
                  <div className={cn(
                    "absolute inset-0 bg-white z-10 transition-transform duration-300 md:hidden",
                    state.activeVideoId ? "translate-x-[-100%]" : "translate-x-0"
                  )}>
                    <div className="h-full overflow-y-auto px-4 pt-4">
                      <div className="grid grid-cols-2 gap-3 pb-6">
                        {state.selectedVideos.map((videoId, index) => (
                          <VideoListItemMobile
                            key={videoId}
                            video={state.videos.find(v => v.id === videoId)}
                            index={index}
                            hasCaption={Boolean(state.captions[videoId])}
                            onClick={() => dispatch({ type: 'SET_ACTIVE_VIDEO', payload: videoId })}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Main Content Area */}
                  <div className={cn(
                    "flex-1 min-w-0 transition-transform duration-300",
                    !state.activeVideoId && "md:translate-x-0 translate-x-[100%]"
                  )}>
                    {state.activeVideoId ? (
                      <div className="h-full flex flex-col">
                        {/* Mobile Content */}
                        <div className="md:hidden flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom,_0px)_+_80px)]">
                          {/* Video Preview */}
                          <div className="aspect-[9/16] relative bg-black">
                            <img
                              src={state.videos.find(v => v.id === state.activeVideoId)?.cover_image_url}
                              alt="Video preview"
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Caption Section */}
                          <div className="p-4 space-y-6">
                            {/* Quick Templates */}
                            <div className="space-y-3">
                              <h4 className="text-base font-medium text-gray-900">Quick Templates</h4>
                              <div className="space-y-2">
                                <CaptionTemplates 
                                  onApplyTemplate={(template) => {
                                    dispatch({
                                      type: 'UPDATE_CAPTION',
                                      payload: { id: state.activeVideoId, content: template }
                                    });
                                  }}
                                />
                              </div>
                            </div>

                            {/* Caption Input */}
                            <div className="bg-card rounded-xl p-4 border border-border mb-4">
                              <div className="flex items-center justify-between mb-3">
                                <label className="text-sm font-medium text-foreground">Caption</label>
                                <span className="text-xs text-muted-foreground">
                                  {(state.captions[state.activeVideoId] || '').length} characters
                                </span>
                              </div>
                              <Textarea
                                value={state.captions[state.activeVideoId] || ''}
                                onChange={(e) => dispatch({
                                  type: 'UPDATE_CAPTION',
                                  payload: { id: state.activeVideoId, content: e.target.value }
                                })}
                                placeholder="Write your caption..."
                                className="min-h-[120px] resize-none rounded-lg border-border focus:border-primary focus:ring-primary bg-background"
                              />
                            </div>

                            {/* Hashtag Suggestions */}
                            <div className="bg-muted rounded-xl p-4 mb-6">
                              <label className="text-sm font-medium text-foreground mb-3 block">Suggested Hashtags</label>
                              <div className="flex flex-wrap gap-2">
                                <HashtagSuggestions 
                                  platform={selectedPlatforms[0]}
                                  onSelect={(tag) => {
                                    const currentCaption = state.captions[state.activeVideoId] || '';
                                    dispatch({
                                      type: 'UPDATE_CAPTION',
                                      payload: { 
                                        id: state.activeVideoId, 
                                        content: currentCaption + ' ' + tag 
                                      }
                                    });
                                  }}
                                  className="inline-flex items-center px-2.5 py-1 rounded-lg bg-card text-sm font-medium text-foreground hover:bg-muted transition-colors"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Desktop Layout */}
                        <div className="hidden md:flex flex-col h-full overflow-y-auto pb-24">
                          <div className="flex flex-row gap-6">
                            {/* Active Video Preview */}
                            <div className="w-[320px] lg:w-[360px] flex-shrink-0">
                              <VideoCard 
                                video={state.videos.find(v => v.id === state.activeVideoId)}
                                isSelected={false}
                                onSelect={() => {}}
                              />
                            </div>

                            {/* Quick Templates */}
                            <div className="flex-1">
                              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                                <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Templates</h4>
                                <div className="grid grid-cols-1 gap-3">
                                  <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:border-purple-200 transition-colors cursor-pointer group">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="text-gray-600 text-sm">✨ Check out this video!</p>
                                        <div className="mt-1 flex gap-1">
                                          <Badge variant="secondary" className="bg-gray-100 text-xs">#trending</Badge>
                                          <Badge variant="secondary" className="bg-gray-100 text-xs">#viral</Badge>
                                        </div>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => dispatch({
                                          type: 'UPDATE_CAPTION',
                                          payload: { id: state.activeVideoId, content: "✨ Check out this video!\n\n#trending #viral" }
                                        })}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        Apply
                                      </Button>
                                    </div>
                                  </div>

                                  <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:border-purple-200 transition-colors cursor-pointer group">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="text-gray-600 text-sm">🔥 Like if you agree!<br/>Comment your thoughts below 👇</p>
                                        <div className="mt-1 flex gap-1">
                                          <Badge variant="secondary" className="bg-gray-100 text-xs">#engagement</Badge>
                                          <Badge variant="secondary" className="bg-gray-100 text-xs">#community</Badge>
                                        </div>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => dispatch({
                                          type: 'UPDATE_CAPTION',
                                          payload: { id: state.activeVideoId, content: "🔥 Like if you agree!\nComment your thoughts below 👇\n\n#engagement #community" }
                                        })}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        Apply
                                      </Button>
                                    </div>
                                  </div>

                                  <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:border-purple-200 transition-colors cursor-pointer group">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="text-gray-600 text-sm">❓ What do you think about this?<br/>Let me know in the comments!</p>
                                        <div className="mt-1 flex gap-1">
                                          <Badge variant="secondary" className="bg-gray-100 text-xs">#question</Badge>
                                          <Badge variant="secondary" className="bg-gray-100 text-xs">#discussion</Badge>
                                        </div>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => dispatch({
                                          type: 'UPDATE_CAPTION',
                                          payload: { id: state.activeVideoId, content: "❓ What do you think about this?\nLet me know in the comments!\n\n#question #discussion" }
                                        })}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        Apply
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Caption Editor */}
                              <div className="bg-card rounded-xl p-4 border border-border mb-4">
                                <div className="flex items-center justify-between mb-3">
                                  <label className="text-sm font-medium text-foreground">Caption</label>
                                  <span className="text-xs text-muted-foreground">
                                    {(state.captions[state.activeVideoId] || '').length} characters
                                  </span>
                                </div>
                                <Textarea
                                  value={state.captions[state.activeVideoId] || ''}
                                  onChange={(e) => dispatch({
                                    type: 'UPDATE_CAPTION',
                                    payload: { id: state.activeVideoId, content: e.target.value }
                                  })}
                                  placeholder="Write your caption..."
                                  className="min-h-[120px] resize-none rounded-lg border-border focus:border-primary focus:ring-primary bg-background"
                                />
                              </div>

                              {/* Desktop Hashtag Suggestions */}
                              <div className="bg-muted rounded-xl p-4 mb-6">
                                <label className="text-sm font-medium text-foreground mb-3 block">Suggested Hashtags</label>
                                <div className="flex flex-wrap gap-2">
                                  <HashtagSuggestions 
                                    platform={selectedPlatforms[0]}
                                    onSelect={(tag) => {
                                      const currentCaption = state.captions[state.activeVideoId] || '';
                                      dispatch({
                                        type: 'UPDATE_CAPTION',
                                        payload: { 
                                          id: state.activeVideoId, 
                                          content: currentCaption + ' ' + tag 
                                        }
                                      });
                                    }}
                                    className="inline-flex items-center px-2.5 py-1 rounded-lg bg-card text-sm font-medium text-foreground hover:bg-muted transition-colors"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="h-full flex items-center justify-center text-gray-500">
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                            <ListVideo className="h-8 w-8 text-gray-400" />
                          </div>
                          <p className="text-lg font-medium text-gray-600">Select a video to start</p>
                          <p className="text-sm text-gray-500 mt-1">Choose a video from the list to edit its caption</p>
                        </div>
                  </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {state.currentStep === 1 && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                key="step-3"
                className="p-6"
              >
                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold">Platforms</h3>
                    <PlatformToggle 
                      platforms={['instagram', 'tiktok', 'youtube']}
                      selected={selectedPlatforms}
                      onChange={setSelectedPlatforms}
                    />
                  </div>
                  <div className="lg:col-span-2 space-y-6">
                    <h3 className="text-xl font-semibold">Schedule</h3>
                    <div className="w-full">
                      <div className="inline-flex h-10 items-center justify-center rounded-lg bg-gray-100 p-1 text-gray-500">
                        <button
                          onClick={() => setScheduleType('batch')}
                          className={cn(
                            "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all",
                            scheduleType === 'batch' 
                              ? "bg-white text-gray-900 shadow-sm" 
                              : "hover:bg-gray-50"
                          )}
                        >
                          Batch Schedule
                        </button>
                        <button
                          onClick={() => setScheduleType('individual')}
                          className={cn(
                            "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all",
                            scheduleType === 'individual' 
                              ? "bg-white text-gray-900 shadow-sm" 
                              : "hover:bg-gray-50"
                          )}
                        >
                          Individual Times
                        </button>
                        <button
                          onClick={() => setScheduleType('automation')}
                          className={cn(
                            "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all gap-1.5",
                            scheduleType === 'automation' 
                              ? "bg-white text-gray-900 shadow-sm" 
                              : "hover:bg-gray-50"
                          )}
                        >
                          <Zap className="w-4 h-4" />
                          Auto Schedule
                        </button>
                      </div>
                      
                      <div className="mt-6">
                        {scheduleType === 'batch' && (
                          <ScheduleCalendar 
                            platforms={selectedPlatforms}
                            onChange={(schedules) => 
                              dispatch({ type: 'SET_SCHEDULE', payload: schedules })
                            }
                          />
                        )}
                        
                        {scheduleType === 'individual' && (
                          <div className="flex gap-6">
                            {/* Video List Sidebar */}
                            <div className="w-72 lg:w-80 flex-shrink-0 border-r border-gray-100">
                              <div className="pr-6 space-y-3 h-full overflow-y-auto">
                                {state.selectedVideos.map((videoId, index) => (
                                  <VideoListItem 
                                    key={videoId}
                                    video={state.videos.find(v => v.id === videoId)}
                                    index={index}
                                    isActive={state.activeVideoId === videoId}
                                    hasCaption={Boolean(state.captions[videoId])}
                                    onClick={() => dispatch({ type: 'SET_ACTIVE_VIDEO', payload: videoId })}
                                  />
                                ))}
                              </div>
                            </div>

                            {/* Scheduling Content Area */}
                            <div className="flex-1">
                              {state.activeVideoId ? (
                                <div className="space-y-6">
                                  {/* Video Preview */}
                                  <div className="w-[320px] lg:w-[360px]">
                                    <VideoCard 
                                      video={state.videos.find(v => v.id === state.activeVideoId)}
                                      isSelected={false}
                                      onSelect={() => {}}
                                    />
                                  </div>

                                  {/* Platform Scheduling */}
                                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                                    <h4 className="text-base font-medium text-gray-900 mb-4">Schedule for Each Platform</h4>
                                    <div className="space-y-4">
                                      {selectedPlatforms.map(platform => (
                                        <div key={platform} className="space-y-2">
                                          <label className="block text-sm font-medium text-gray-700 capitalize">
                                            {platform}
                                          </label>
                                          <input
                                            type="datetime-local"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                            onChange={(e) => setCustomTimes(prev => ({
                                              ...prev,
                                              [state.activeVideoId]: {
                                                ...prev[state.activeVideoId],
                                                [platform]: e.target.value
                                              }
                                            }))}
                                            value={customTimes[state.activeVideoId]?.[platform] || ''}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="h-full flex items-center justify-center text-gray-500">
                                  <div className="text-center">
                                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                                      <Clock className="h-8 w-8 text-gray-400" />
                                    </div>
                                    <p className="text-lg font-medium text-gray-600">Select a video to schedule</p>
                                    <p className="text-sm text-gray-500 mt-1">Choose a video from the list to set its schedule</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {scheduleType === 'automation' && (
                          <div className="space-y-6">
                            {/* Automation Type Selection */}
                            <div className="flex gap-4">
                              <button
                                onClick={() => setAutomationType('new')}
                                className={cn(
                                  "flex-1 p-4 rounded-xl border transition-all",
                                  automationType === 'new' 
                                    ? "border-purple-200 bg-purple-50 shadow-sm" 
                                    : "border-gray-200 bg-white hover:bg-gray-50"
                                )}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="p-2 rounded-lg bg-white shadow-sm border border-purple-100">
                                    <Zap className="w-5 h-5 text-purple-600" />
                                  </div>
                                  <div className="text-left">
                                    <h4 className="font-medium text-gray-900">Create New Automation</h4>
                                    <p className="text-sm text-gray-500">Set up a new automated posting schedule</p>
                                  </div>
                                </div>
                              </button>

                              <button
                                onClick={() => setAutomationType('existing')}
                                className={cn(
                                  "flex-1 p-4 rounded-xl border transition-all",
                                  automationType === 'existing' 
                                    ? "border-purple-200 bg-purple-50 shadow-sm" 
                                    : "border-gray-200 bg-white hover:bg-gray-50"
                                )}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="p-2 rounded-lg bg-white shadow-sm border border-purple-100">
                                    <ListVideo className="w-5 h-5 text-purple-600" />
                                  </div>
                                  <div className="text-left">
                                    <h4 className="font-medium text-gray-900">Add to Existing</h4>
                                    <p className="text-sm text-gray-500">Add to an existing automation</p>
                                  </div>
                                </div>
                              </button>
                            </div>

                            {automationType === 'new' ? (
                              <>
                                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
                                  <div className="flex items-start gap-4">
                                    <div className="p-3 rounded-lg bg-white shadow-sm border border-purple-100">
                                      <Zap className="w-6 h-6 text-purple-600" />
                                    </div>
                                    <div className="space-y-6 flex-1">
                                      <div>
                                        <h4 className="text-lg font-semibold text-gray-900">Automation Details</h4>
                                        <p className="text-gray-600 mt-1">Set up your automated posting schedule</p>
                                      </div>

                                      <div className="space-y-4">
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Name
                                          </label>
                                          <Input
                                            placeholder="e.g., Peak Hours Posting"
                                            value={newAutomationData.name}
                                            onChange={(e) => setNewAutomationData(prev => ({
                                              ...prev,
                                              name: e.target.value
                                            }))}
                                            className="max-w-md"
                                          />
                                        </div>

                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Description
                                          </label>
                                          <Textarea
                                            placeholder="Describe your automation strategy..."
                                            value={newAutomationData.description}
                                            onChange={(e) => setNewAutomationData(prev => ({
                                              ...prev,
                                              description: e.target.value
                                            }))}
                                            className="max-w-md h-24"
                                          />
                                        </div>

                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Goal
                                          </label>
                                          <div className="flex flex-wrap gap-2">
                                            {[
                                              { value: 'reach', label: 'Maximize Reach', icon: <Zap className="w-4 h-4" /> },
                                              { value: 'engagement', label: 'Drive Engagement', icon: <Users className="w-4 h-4" /> },
                                              { value: 'consistency', label: 'Maintain Consistency', icon: <Calendar className="w-4 h-4" /> }
                                            ].map(goal => (
                                              <button
                                                key={goal.value}
                                                onClick={() => setNewAutomationData(prev => ({ ...prev, goal: goal.value }))}
                                                className={cn(
                                                  "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                                                  newAutomationData.goal === goal.value
                                                    ? "bg-purple-100 text-purple-700"
                                                    : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                                                )}
                                              >
                                                {goal.icon}
                                                {goal.label}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="space-y-4">
                                <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                                  <div className="flex items-center gap-2 text-purple-700">
                                    <Zap className="h-4 w-4" />
                                    <span className="text-sm font-medium">
                                      {state.selectedVideos.length} video{state.selectedVideos.length !== 1 ? 's' : ''} will be added to your automation
                                    </span>
                                  </div>
                                </div>

                                {/* Existing Automations Grid */}
                                <div className="grid gap-4">
                                  {isLoadingAutomations ? (
                                    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-600 mb-4" />
                                      <p className="text-gray-600">Loading your automations...</p>
                                    </div>
                                  ) : automations.length > 0 ? (
                                    automations.map((automation) => (
                                      <button
                                        key={automation.id}
                                        onClick={() => setSelectedAutomation(automation.id)}
                                        className={cn(
                                          "w-full text-left p-6 rounded-xl border transition-all",
                                          selectedAutomation === automation.id 
                                            ? "border-purple-200 bg-purple-50" 
                                            : "border-gray-200 bg-white hover:bg-gray-50"
                                        )}
                                      >
                                        <div className="flex items-start justify-between">
                                          <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-xl bg-white shadow-sm border border-purple-100">
                                              {automation.goal === 'reach' && <Zap className="h-5 w-5 text-purple-600" />}
                                              {automation.goal === 'engagement' && <Users className="h-5 w-5 text-purple-600" />}
                                              {automation.goal === 'consistency' && <Calendar className="h-5 w-5 text-purple-600" />}
                                            </div>
                                            <div>
                                              <h3 className="font-medium text-gray-900">{automation.name}</h3>
                                              <p className="text-sm text-gray-500">{automation.description}</p>
                                            </div>
                                          </div>
                                          {selectedAutomation === automation.id && (
                                            <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center">
                                              <CheckCircle2 className="w-4 h-4 text-purple-600" />
                                            </div>
                                          )}
                                        </div>
                                        <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
                                          <div className="flex items-center gap-1">
                                            <Clock className="h-4 w-4" />
                                            <span>{automation.schedule}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <Calendar className="h-4 w-4" />
                                            <span>{automation.nextPost}</span>
                                          </div>
                                        </div>
                                      </button>
                                    ))
                                  ) : (
                                    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                                      <div className="mx-auto w-16 h-16 rounded-full bg-purple-50 flex items-center justify-center mb-4">
                                        <Zap className="h-8 w-8 text-purple-500" />
                                      </div>
                                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Automations Yet</h3>
                                      <p className="text-gray-500 mb-6">Create your first automation to start scheduling content</p>
                                      <Button
                                        onClick={() => setAutomationType('new')}
                                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                                      >
                                        Create Your First Automation
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Keep existing Summary Card */}
                            <div className="bg-gray-50 rounded-xl p-6">
                              <h4 className="text-base font-medium text-gray-900 mb-3">Schedule Summary</h4>
                              <div className="space-y-2">
                                <p className="text-sm text-gray-600">
                                  • {state.selectedVideos.length} videos will be {automationType === 'new' ? 'scheduled' : 'added to automation'}
                                </p>
                                <p className="text-sm text-gray-600">
                                  • Posts will be optimized for {selectedPlatforms.length} platform{selectedPlatforms.length > 1 ? 's' : ''}
                                </p>
                                {automationType === 'new' && (
                                <p className="text-sm text-gray-600">
                                  • Scheduling will start from tomorrow
                                </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {state.currentStep === 2 && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                key="step-4"
                className="p-6"
              >
                <div className="space-y-8">
                  <div className="bg-purple-50 p-6 rounded-xl">
                    <h3 className="text-xl font-bold mb-4">Post Summary</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="p-4 bg-white rounded-lg">
                        <div className="text-gray-500 mb-1">Videos</div>
                        <div className="text-2xl font-bold">
                          {state.selectedVideos.length}
                        </div>
                      </div>
                      <div className="p-4 bg-white rounded-lg">
                        <div className="text-gray-500 mb-1">Platforms</div>
                        <div className="space-y-1">
                          {selectedPlatforms.map(platform => (
                            <Badge key={platform} className="mr-2">
                              {platform}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="p-4 bg-white rounded-lg">
                        <div className="text-gray-500 mb-1">Schedule Type</div>
                        <div className="font-medium capitalize">
                          {scheduleType}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handlePost}
                    className="w-full py-6 text-lg"
                    disabled={isPosting}
                  >
                    {isPosting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Posting {state.postingProgress}%
                      </>
                    ) : (
                      <>
                        <Zap className="mr-2 h-5 w-5" />
                        Confirm & Schedule Posts
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Fixed Navigation Bar */}
          {state.currentStep === 0 && state.activeVideoId && (
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="fixed bottom-0 left-0 right-0 z-[9999] pb-safe"
            >
              <div className="bg-card border-t border-border p-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                  <Button
                    variant="ghost"
                    onClick={() => dispatch({ type: 'SET_ACTIVE_VIDEO', payload: null })}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Back to List
                  </Button>
                  
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="hidden md:flex bg-muted text-muted-foreground">
                      {state.selectedVideos.filter(id => state.captions[id]).length} / {state.selectedVideos.length} Captioned
                    </Badge>
                    <Button
                      onClick={() => dispatch({ type: 'SET_STEP', payload: state.currentStep + 1 })}
                      disabled={Object.keys(state.captions).length === 0}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Next Step
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Static Navigation Bar for other steps */}
          {state.currentStep !== 0 && (
            <div className="mt-8 flex justify-between p-6 border-t border-border">
            <Button
              variant="outline"
              disabled={state.currentStep === 0}
              onClick={() => dispatch({ type: 'SET_STEP', payload: state.currentStep - 1 })}
              className="hover:bg-muted border-border"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
                Previous Step
            </Button>
            
            {state.currentStep < steps.length - 1 && (
              <Button
                onClick={() => dispatch({ type: 'SET_STEP', payload: state.currentStep + 1 })}
                disabled={Object.keys(state.captions).length === 0}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                  Next Step
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
          )}
        </motion.div>

        {showSuccess && <SuccessState />}
      </div>
    </div>
  );
}

const VideoListItem = ({ video, index, isActive, hasCaption, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      "w-full text-left p-2 rounded-lg transition-colors",
      "hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/20",
      isActive ? "bg-primary/10 border-primary/20" : "border-transparent",
      "relative group"
    )}
  >
    <div className="flex items-center gap-3">
      <div className="w-20 h-36 rounded-md overflow-hidden flex-shrink-0">
        <img
          src={video.cover_image_url}
          alt={`Video ${index + 1}`}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">Video {index + 1}</p>
        <Badge 
          className={cn(
            "mt-1",
            hasCaption ? "bg-green-500/20 text-green-500" : "bg-muted text-muted-foreground"
          )}
        >
          {hasCaption ? "Caption Added" : "No Caption"}
        </Badge>
      </div>
    </div>
  </button>
);

const VideoListItemMobile = ({ video, index, hasCaption, onClick }) => (
  <button
    onClick={onClick}
    className="w-full aspect-[9/16] relative rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary"
  >
    <img
      src={video.cover_image_url}
      alt={`Video ${index + 1}`}
      className="w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
    <div className="absolute bottom-2 left-2 right-2">
      <p className="text-foreground text-sm font-medium mb-1">Video {index + 1}</p>
      <Badge 
        className={cn(
          "w-full justify-center",
          hasCaption ? "bg-green-500/20 text-green-500" : "bg-muted text-muted-foreground"
        )}
      >
        {hasCaption ? "Caption Added" : "No Caption"}
      </Badge>
    </div>
  </button>
);

export default MultiCrossPostWizard; 